// Service Worker Registration and Management
// This file handles PWA service worker registration and lifecycle

import logger from '../core/logger';
import { isNative } from '../core/platform';

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  window.location.hostname === '[::1]' ||
  window.location.hostname.match(
    /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
  )
);

export function register(config) {
  // Service workers are not supported / needed in Capacitor native apps.
  if (isNative()) return;

  if ('serviceWorker' in navigator) {
    const publicUrl = new URL(import.meta.env.BASE_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${import.meta.env.BASE_URL}service-worker.js`;

      if (isLocalhost) {
        checkValidServiceWorker(swUrl, config);
        navigator.serviceWorker.ready.then(() => {
          logger.debug(
            'This web app is being served cache-first by a service ' +
            'worker. To learn more, visit https://cra.link/PWA'
          );
        });
      } else {
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      logger.debug('SW registered: ', registration);

      // Check for updates
      registration.addEventListener('updatefound', () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }

        installingWorker.addEventListener('statechange', () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              logger.info(
                'New content is available and will be used when all ' +
                'tabs for this page are closed. See https://cra.link/PWA.'
              );

              // Execute callback
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              logger.info('Content is cached for offline use.');

              // Execute callback
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        });
      });

      // Listen for messages from service worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        logger.debug('Message from SW:', event.data);

        if (event.data && event.data.type) {
          // Known types (TRIGGER_SYNC) are handled elsewhere — only log truly unexpected ones
          const knownTypes = ['TRIGGER_SYNC'];
          if (!knownTypes.includes(event.data.type)) {
            logger.debug('Unknown message type:', event.data.type);
          }
        }
      });

      // Register for background sync if supported
      if ('sync' in window.ServiceWorkerRegistration.prototype) {
        registration.sync.register('journal-sync').catch((error) => {
          logger.debug('Background sync registration failed:', error);
        });
      }

    })
    .catch((error) => {
      logger.error('Error during service worker registration:', error);
    });
}

function checkValidServiceWorker(swUrl, config) {
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      logger.info('No internet connection found. App is running in offline mode.');
    });
}

export function unregister() {
  if (isNative()) return;
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        logger.error(error.message);
      });
  }
}

// Network status monitoring
export function initNetworkMonitoring() {
  if ('navigator' in window && 'onLine' in navigator) {
    const updateOnlineStatus = () => {
      const isOnline = navigator.onLine;
      logger.debug('Network status:', isOnline ? 'online' : 'offline');

      // Dispatch custom event for app to handle
      window.dispatchEvent(new CustomEvent('networkstatus', {
        detail: { online: isOnline }
      }));

      if (isOnline) {
        // Trigger background sync when coming back online (web-only)
        if (!isNative() && 'serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
          navigator.serviceWorker.ready.then(registration => {
            return registration.sync.register('journal-sync');
          }).catch(error => {
            logger.debug('Background sync registration failed:', error);
          });
        }
      }
    };

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    // Initial check
    updateOnlineStatus();
  }
}

// Push notifications are handled by FCM (see src/shared/utils/notifications/fcm.js)
// The dedicated firebase-messaging-sw.js handles background push events